import { MyVideoPackageListSchema, ProjectPackageRefListSchema, VideoContentListSchema, VideoExecutorOptionsSchema,
  VideoPackageListResponseSchema, VideoPackageOptionsSchema, VideoPackageWriteResponseSchema,
  VideoPackageCompletionPreviewSchema,
  VideoResultStageReceiptSchema, VideoResultReceiptSchema, VideoResultListSchema,
  VideoDeliveryReceiptSchema, VideoDeliveryDetailSchema,
  VideoContentAuditSchema,
  ContentIssueListSchema, ContentIssueWriteResponseSchema,
  UpdateContentTitleResponseSchema,
  UpdateContentPublicationInfoResponseSchema,
  ContentSharedAttachmentListSchema, ContentSharedAttachmentWriteResponseSchema,
  ContentSharedAttachmentStageSchema, CreateContentSharedAttachmentRequestSchema,
  UpdateContentSharedAttachmentRequestSchema, RetractContentSharedAttachmentRequestSchema,
  type ContentSharedAttachmentList, type ContentSharedAttachmentWriteResponse,
  type ContentSharedAttachmentStage, type CreateContentSharedAttachmentRequest,
  type UpdateContentSharedAttachmentRequest, type RetractContentSharedAttachmentRequest,
  type UpdateContentPublicationInfoResponse,
  type VideoPackageDraftRequest, type VideoPackageUpdateDraftRequest, type VideoPackageDefinitionUpdateRequest,
  type VideoPackageListResponse, type VideoPackageOptions,
  type VideoPackageCompletionPreview,
  type VideoPackageWriteResponse, type VideoContentList, type VideoExecutorOptions,
  type MyVideoPackageList, type ProjectPackageRefList,
  type VideoResultStageReceipt, type VideoResultReceipt, type VideoResultList,
  type VideoDeliveryReceipt, type VideoContentAudit, type ContentIssueList, type ContentIssueWriteResponse,
  type CreateContentIssueRequest,
  type UpdateContentTitleResponse } from '@youkuai/contracts';

import { apiDownload, apiRequest } from './project-api.ts';

/** 服务包只有一套资源路径；内容类型是筛选条件和资源字段，不再复制路由。 */
export type PackageContentType = 'VIDEO' | 'ARTICLE';

function base(projectId: string, _contentType: PackageContentType = 'VIDEO'): string {
  return `/projects/${encodeURIComponent(projectId)}/service-packages`;
}

export async function listVideoPackages(token: string, projectId: string,
  contentType: PackageContentType = 'VIDEO'): Promise<VideoPackageListResponse> {
  const response = VideoPackageListResponseSchema.parse(await apiRequest(
    `${base(projectId)}?contentType=${encodeURIComponent(contentType)}`, token));
  return { ...response, items: response.items.filter((item) => item.contentType === contentType) };
}

export async function listMyVideoPackages(token: string, projectId: string,
  contentType: PackageContentType = 'VIDEO'): Promise<MyVideoPackageList> {
  return MyVideoPackageListSchema.parse(await apiRequest(
    `${base(projectId)}/mine?contentType=${encodeURIComponent(contentType)}`, token));
}

/** BUG-0172：没有「服务包」页权限的成员只取最小包标识，供内容/任务页分组。 */
export async function listProjectPackageRefs(token: string, projectId: string,
  contentType: PackageContentType = 'VIDEO'): Promise<ProjectPackageRefList> {
  return ProjectPackageRefListSchema.parse(await apiRequest(
    `${base(projectId)}/refs?contentType=${encodeURIComponent(contentType)}`, token));
}

export async function videoPackageOptions(token: string, projectId: string, on: string,
  contentType: PackageContentType = 'VIDEO'): Promise<VideoPackageOptions> {
  return VideoPackageOptionsSchema.parse(
    await apiRequest(`${base(projectId)}/options?on=${encodeURIComponent(on)}&contentType=${encodeURIComponent(contentType)}`, token));
}

export async function draftVideoPackage(input: { token: string; projectId: string; contentType?: PackageContentType;
  key: string; draft: VideoPackageDraftRequest }): Promise<VideoPackageWriteResponse> {
  return VideoPackageWriteResponseSchema.parse(await apiRequest(base(input.projectId, input.contentType ?? 'VIDEO'), input.token, {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.key },
    body: JSON.stringify(input.draft),
  }));
}

export async function updateVideoPackageDraft(input:{token:string;projectId:string;contentType?:PackageContentType;
  packageId:string;key:string;draft:VideoPackageUpdateDraftRequest}):Promise<VideoPackageWriteResponse>{
  return VideoPackageWriteResponseSchema.parse(await apiRequest(
    `${base(input.projectId,input.contentType??'VIDEO')}/${encodeURIComponent(input.packageId)}`,input.token,{
      method:'PATCH',headers:{'Content-Type':'application/json','Idempotency-Key':input.key},body:JSON.stringify(input.draft),
    }));
}

export async function videoPackageDefinitionOptions(input: { token: string; projectId: string;
  packageId: string }): Promise<VideoPackageOptions> {
  return VideoPackageOptionsSchema.parse(await apiRequest(
    `${base(input.projectId)}/${encodeURIComponent(input.packageId)}/definition-options`, input.token));
}

export async function updateVideoPackageDefinition(input: { token: string; projectId: string;
  packageId: string; key: string; change: VideoPackageDefinitionUpdateRequest;
  signal?: AbortSignal }): Promise<VideoPackageWriteResponse> {
  return VideoPackageWriteResponseSchema.parse(await apiRequest(
    `${base(input.projectId)}/${encodeURIComponent(input.packageId)}/definition`, input.token, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.key },
      body: JSON.stringify(input.change), ...(input.signal ? { signal: input.signal } : {}),
    }));
}

async function packageTransition(input:{token:string;projectId:string;contentType?:PackageContentType;packageId:string;
  action:'submit'|'return'|'confirm'|'start'|'complete'|'reopen';key:string;body:Record<string,unknown>}):Promise<VideoPackageWriteResponse>{
  return VideoPackageWriteResponseSchema.parse(await apiRequest(
    `${base(input.projectId,input.contentType??'VIDEO')}/${encodeURIComponent(input.packageId)}/${input.action}`,input.token,{
      method:'POST',headers:{'Content-Type':'application/json','Idempotency-Key':input.key},body:JSON.stringify(input.body),
    }));
}

export function submitVideoPackage(input:{token:string;projectId:string;contentType?:PackageContentType;packageId:string;
  key:string;expectedVersion:number}):Promise<VideoPackageWriteResponse>{
  return packageTransition({...input,action:'submit',body:{expectedVersion:input.expectedVersion}});
}

export function returnVideoPackage(input:{token:string;projectId:string;contentType?:PackageContentType;packageId:string;
  key:string;expectedVersion:number;reason:string}):Promise<VideoPackageWriteResponse>{
  return packageTransition({...input,action:'return',body:{expectedVersion:input.expectedVersion,reason:input.reason}});
}

export function confirmVideoPackage(input:{token:string;projectId:string;contentType?:PackageContentType;packageId:string;
  key:string;expectedVersion:number}):Promise<VideoPackageWriteResponse>{
  return packageTransition({...input,action:'confirm',body:{expectedVersion:input.expectedVersion}});
}

export async function startVideoPackage(input: { token: string; projectId: string; contentType?: PackageContentType; packageId: string;
  key: string; expectedVersion: number }): Promise<VideoPackageWriteResponse> {
  return packageTransition({...input,action:'start',body:{expectedVersion:input.expectedVersion}});
}

export async function previewVideoPackageCompletion(token:string,projectId:string,packageId:string,
  contentType:PackageContentType='VIDEO'):Promise<VideoPackageCompletionPreview>{
  return VideoPackageCompletionPreviewSchema.parse(await apiRequest(
    `${base(projectId,contentType)}/${encodeURIComponent(packageId)}/completion-preview`,token));
}

export function completeVideoPackage(input:{token:string;projectId:string;contentType?:PackageContentType;packageId:string;
  key:string;expectedVersion:number;completedOn:string;note:string}):Promise<VideoPackageWriteResponse>{
  return packageTransition({...input,action:'complete',body:{expectedVersion:input.expectedVersion,
    completedOn:input.completedOn,note:input.note}});
}

export function reopenVideoPackage(input:{token:string;projectId:string;contentType?:PackageContentType;packageId:string;
  key:string;expectedVersion:number;reason:string}):Promise<VideoPackageWriteResponse>{
  return packageTransition({...input,action:'reopen',body:{expectedVersion:input.expectedVersion,reason:input.reason}});
}

export async function listVideoContents(token: string, projectId: string, packageId: string,
  after = 0, contentType: PackageContentType = 'VIDEO'): Promise<VideoContentList> {
  return VideoContentListSchema.parse(await apiRequest(`${base(projectId, contentType)}/${encodeURIComponent(packageId)}/contents?after=${after}`, token));
}

/** 服务端内容分页固定为每页 50 条，游标是上一页最后一条的 ordinal。 */
export const CONTENT_PAGE_SIZE = 50;
const MAX_CONTENT_PAGES = 200;

/**
 * 读取服务包内的全部内容。工作区内容中心、交付登记、发布计划和费用记录都以完整清单为前提：
 * 只取第一页会让第 51 条起的内容看不见，也进不了交付登记（BUG-0079）。
 */
export async function listAllVideoContents(token: string, projectId: string, packageId: string,
  contentType: PackageContentType = 'VIDEO'): Promise<VideoContentList> {
  const items: VideoContentList['items'] = [];
  const seen = new Set<string>();
  let after = 0;
  for (let page = 0; page < MAX_CONTENT_PAGES; page += 1) {
    const chunk = await listVideoContents(token, projectId, packageId, after, contentType);
    for (const item of chunk.items) {
      if (seen.has(item.contentId)) continue;
      seen.add(item.contentId);
      items.push(item);
    }
    // nextCursor 为空表示读完；游标不前进时也停止，避免异常数据导致无限请求。
    if (chunk.nextCursor === null || chunk.nextCursor <= after) return { items, nextCursor: null };
    after = chunk.nextCursor;
  }
  return { items, nextCursor: null };
}

export async function updateContentTitle(input: { token: string; projectId: string;
  contentType?: PackageContentType; packageId: string; contentId: string; expectedVersion: number;
  title: string; legacyNo?: string | undefined; displayNo?: number | undefined; key: string }): Promise<UpdateContentTitleResponse> {
  return UpdateContentTitleResponseSchema.parse(await apiRequest(
    `${base(input.projectId, input.contentType ?? 'VIDEO')}/${encodeURIComponent(input.packageId)}/contents/` +
    `${encodeURIComponent(input.contentId)}/title`, input.token, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.key },
      body: JSON.stringify({ expectedVersion: input.expectedVersion, title: input.title.trim(),
        ...(input.legacyNo === undefined ? {} : { legacyNo: input.legacyNo.trim() }),
        ...(input.displayNo === undefined ? {} : { displayNo: input.displayNo }) }),
    }));
}

export async function updateContentPublicationInfo(input: { token: string; projectId: string;
  contentType?: PackageContentType; packageId: string; contentId: string; expectedVersion: number;
  publicationTitle: string | null; publicationCopy: string; publicationTags: string[]; key: string }):
  Promise<UpdateContentPublicationInfoResponse> {
  return UpdateContentPublicationInfoResponseSchema.parse(await apiRequest(
    `${base(input.projectId, input.contentType ?? 'VIDEO')}/${encodeURIComponent(input.packageId)}/contents/` +
    `${encodeURIComponent(input.contentId)}/publication-info`, input.token, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.key },
      body: JSON.stringify({ expectedVersion: input.expectedVersion,
        publicationTitle: input.publicationTitle?.trim() || null,
        publicationCopy: input.publicationCopy.trim(),
        publicationTags: input.publicationTags.map((tag) => tag.trim()).filter(Boolean) }),
    }));
}

export async function listVideoContentAudit(token: string, projectId: string, packageId: string,
  contentId: string, contentType: PackageContentType = 'VIDEO'): Promise<VideoContentAudit> {
  return VideoContentAuditSchema.parse(await apiRequest(
    `${base(projectId, contentType)}/${encodeURIComponent(packageId)}/contents/${encodeURIComponent(contentId)}/audit`, token));
}

export async function videoTaskExecutors(token: string, projectId: string,
  packageId: string, contentType: PackageContentType = 'VIDEO'): Promise<VideoExecutorOptions> {
  return VideoExecutorOptionsSchema.parse(await apiRequest(`${base(projectId, contentType)}/${encodeURIComponent(packageId)}/executors`, token));
}

function resultsPath(input: { projectId: string; packageId: string; contentId: string; taskId: string;
  contentType?: PackageContentType }): string {
  return `${base(input.projectId, input.contentType ?? 'VIDEO')}/${encodeURIComponent(input.packageId)}/contents/` +
    `${encodeURIComponent(input.contentId)}/tasks/${encodeURIComponent(input.taskId)}/results`;
}

// 统一 service-packages 前缀后，视频/文章成果共用同一组路由，内容类型通过 query 声明。
function resultsUrl(input: { projectId: string; packageId: string; contentId: string; taskId: string;
  contentType?: PackageContentType }): string {
  return `${resultsPath(input)}?contentType=${encodeURIComponent(input.contentType ?? 'VIDEO')}`;
}

function deliveryUrl(input: { projectId: string; packageId: string; contentId: string;
  contentType?: PackageContentType }): string {
  return `${base(input.projectId, input.contentType ?? 'VIDEO')}/${encodeURIComponent(input.packageId)}/contents/` +
    `${encodeURIComponent(input.contentId)}/delivery?contentType=${encodeURIComponent(input.contentType ?? 'VIDEO')}`;
}

export async function listVideoResults(input: { token: string; projectId: string; contentType?: PackageContentType; packageId: string;
  contentId: string; taskId: string }): Promise<VideoResultList> {
  return VideoResultListSchema.parse(await apiRequest(resultsUrl(input), input.token));
}

export async function stageVideoResult(input: { token: string; projectId: string; contentType?: PackageContentType; packageId: string;
  contentId: string; taskId: string; expectedVersion: number; key: string; file: File }):
  Promise<VideoResultStageReceipt> {
  const query = new URLSearchParams({ filename: input.file.name, expectedVersion: String(input.expectedVersion),
    contentType: input.contentType ?? 'VIDEO' });
  return VideoResultStageReceiptSchema.parse(await apiRequest(`${resultsPath(input)}/uploads?${query}`, input.token, {
    method: 'POST', headers: { 'Content-Type': input.file.type || 'application/octet-stream',
      'Idempotency-Key': input.key }, body: input.file,
  }));
}

export async function submitVideoResult(input: { token: string; projectId: string; contentType?: PackageContentType; packageId: string;
  contentId: string; taskId: string; expectedVersion: number; key: string; stagingId: string }):
  Promise<VideoResultReceipt> {
  return VideoResultReceiptSchema.parse(await apiRequest(resultsUrl(input), input.token, {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.key },
    body: JSON.stringify({ expectedVersion: input.expectedVersion, stagingId: input.stagingId }),
  }));
}

export async function downloadVideoResult(input: { token: string; projectId: string; contentType?: PackageContentType; packageId: string;
  contentId: string; taskId: string; resultId: string }): Promise<Blob> {
  return apiDownload(`${resultsUrl(input)}/${encodeURIComponent(input.resultId)}/download`, input.token);
}

export async function acceptVideoDelivery(input: { token: string; projectId: string; contentType?: PackageContentType; packageId: string;
  contentId: string; expectedVersion: number; resultId: string; businessOn: string; channel: string;
  key: string }): Promise<VideoDeliveryReceipt> {
  return VideoDeliveryReceiptSchema.parse(await apiRequest(
    `${deliveryUrl(input)}/accept`, input.token,
    { method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.key },
      body: JSON.stringify({ expectedVersion: input.expectedVersion, resultId: input.resultId,
        businessOn: input.businessOn, channel: input.channel }) }));
}

function issuesPath(input: { projectId: string; packageId: string; contentId: string;
  contentType?: PackageContentType }): string {
  return `${base(input.projectId, input.contentType ?? 'VIDEO')}/${encodeURIComponent(input.packageId)}/contents/` +
    `${encodeURIComponent(input.contentId)}/issues`;
}

export async function listContentIssues(input: { token: string; projectId: string; packageId: string;
  contentId: string; contentType?: PackageContentType }): Promise<ContentIssueList> {
  return ContentIssueListSchema.parse(await apiRequest(issuesPath(input), input.token));
}

export async function listMyContentIssues(token: string, status = 'OPEN'): Promise<ContentIssueList> {
  return ContentIssueListSchema.parse(await apiRequest(`/me/content-issues?status=${encodeURIComponent(status)}`, token));
}

export async function createContentIssue(input: { token: string; projectId: string; packageId: string;
  contentId: string; contentType?: PackageContentType; key: string; issue: CreateContentIssueRequest }):
  Promise<ContentIssueWriteResponse> {
  return ContentIssueWriteResponseSchema.parse(await apiRequest(issuesPath(input), input.token, {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.key },
    body: JSON.stringify(input.issue),
  }));
}

export function downloadContentIssueAttachment(input: { token: string; projectId: string; packageId: string;
  contentId: string; contentType?: PackageContentType; issueId: string; attachmentId: string }): Promise<Blob> {
  return apiDownload(`${issuesPath(input)}/${encodeURIComponent(input.issueId)}/attachments/` +
    `${encodeURIComponent(input.attachmentId)}/download`, input.token);
}

export async function updateContentIssue(input: { token: string; projectId: string; packageId: string;
  contentId: string; contentType?: PackageContentType; issueId: string; action: 'assignee' | 'blocking' | 'resolve' | 'reopen' | 'void';
  key: string; body: Record<string, unknown> }): Promise<ContentIssueWriteResponse> {
  const method = input.action === 'assignee' || input.action === 'blocking' ? 'PATCH' : 'POST';
  return ContentIssueWriteResponseSchema.parse(await apiRequest(
    `${issuesPath(input)}/${encodeURIComponent(input.issueId)}/${input.action}`, input.token, {
      method, headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.key },
      body: JSON.stringify(input.body),
    }));
}

export async function getVideoDelivery(input: { token: string; projectId: string; contentType?: PackageContentType; packageId: string;
  contentId: string }): Promise<{ delivery: VideoDeliveryReceipt | null }> {
  const payload = await apiRequest(deliveryUrl(input), input.token);
  return VideoDeliveryDetailSchema.parse(payload);
}


// BUG-0169：内容级共享附件（文件 / 外部链接）。绑定 contentId，不绑 taskId。
function sharedBase(projectId: string, contentId: string): string {
  return `/projects/${encodeURIComponent(projectId)}/contents/${encodeURIComponent(contentId)}/shared-attachments`;
}

export async function listContentSharedAttachments(token: string, projectId: string, contentId: string):
  Promise<ContentSharedAttachmentList> {
  return ContentSharedAttachmentListSchema.parse(await apiRequest(sharedBase(projectId, contentId), token));
}

export async function stageContentSharedAttachment(input: { token: string; projectId: string; contentId: string;
  key: string; file: File }): Promise<ContentSharedAttachmentStage> {
  return ContentSharedAttachmentStageSchema.parse(await apiRequest(
    `${sharedBase(input.projectId, input.contentId)}/uploads?filename=${encodeURIComponent(input.file.name)}`,
    input.token, { method: 'POST', headers: { 'Content-Type': input.file.type || 'application/octet-stream',
      'Idempotency-Key': input.key }, body: await input.file.arrayBuffer() }));
}

export async function createContentSharedAttachment(input: { token: string; projectId: string; contentId: string;
  key: string; request: CreateContentSharedAttachmentRequest }): Promise<ContentSharedAttachmentWriteResponse> {
  const request = CreateContentSharedAttachmentRequestSchema.parse(input.request);
  return ContentSharedAttachmentWriteResponseSchema.parse(await apiRequest(sharedBase(input.projectId, input.contentId),
    input.token, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.key },
      body: JSON.stringify(request) }));
}

export async function updateContentSharedAttachment(input: { token: string; projectId: string; contentId: string;
  attachmentId: string; key: string; request: UpdateContentSharedAttachmentRequest }):
  Promise<ContentSharedAttachmentWriteResponse> {
  const request = UpdateContentSharedAttachmentRequestSchema.parse(input.request);
  return ContentSharedAttachmentWriteResponseSchema.parse(await apiRequest(
    `${sharedBase(input.projectId, input.contentId)}/${input.attachmentId}`, input.token,
    { method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.key },
      body: JSON.stringify(request) }));
}

export async function retractContentSharedAttachment(input: { token: string; projectId: string; contentId: string;
  attachmentId: string; key: string; request: RetractContentSharedAttachmentRequest }):
  Promise<ContentSharedAttachmentWriteResponse> {
  const request = RetractContentSharedAttachmentRequestSchema.parse(input.request);
  return ContentSharedAttachmentWriteResponseSchema.parse(await apiRequest(
    `${sharedBase(input.projectId, input.contentId)}/${input.attachmentId}/retract`, input.token,
    { method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': input.key },
      body: JSON.stringify(request) }));
}

export function contentSharedAttachmentDownloadUrl(projectId: string, contentId: string, attachmentId: string,
  versionNo?: number): string {
  const query = versionNo === undefined ? '' : `?version=${versionNo}`;
  return `/api${sharedBase(projectId, contentId)}/${attachmentId}/download${query}`;
}
